import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookHeart, Trash2, MessageCircleHeart, Sparkles } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

export default function Favorites() {
    const [favorites, setFavorites] = useState([]);

    const load = async () => {
        const { data } = await api.get("/favorites");
        setFavorites(data.favorites || []);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        await api.delete(`/favorites/${id}`);
        setFavorites(prev => prev.filter(f => f.id !== id));
        toast.success("Removed");
    };

    return (
        <div className="max-w-5xl mx-auto p-6 sm:p-10 lg:p-14">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Favorites</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    The words that <span className="font-[300] italic">felt like a hug.</span>
                </h1>
            </motion.div>

            {favorites.length === 0 ? (
                <div className="bg-card rounded-3xl p-12 border border-border shadow-soft text-center" data-testid="favorites-empty">
                    <BookHeart className="w-10 h-10 text-primary mx-auto mb-6" strokeWidth={1.5} />
                    <p className="font-heading text-2xl font-[300] italic mb-3">Nothing saved yet.</p>
                    <p className="text-muted-foreground">When something makes you feel a little lighter, tap "Save" and it'll live here.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="favorites-grid">
                    {favorites.map((f, i) => (
                        <motion.div
                            key={f.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                            className="bg-card rounded-3xl p-7 border border-border shadow-soft group"
                        >
                            <div className="flex items-center gap-2 mb-4">
                                {f.source === "affirmation" ? (
                                    <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                                ) : (
                                    <MessageCircleHeart className="w-4 h-4 text-primary" strokeWidth={1.5} />
                                )}
                                <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                                    {f.source === "affirmation" ? "Affirmation" : "From your chat"}
                                </span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                    {new Date(f.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </span>
                            </div>
                            <p className="leading-relaxed whitespace-pre-wrap mb-4">{f.text}</p>
                            <button onClick={() => remove(f.id)} className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-opacity duration-200" data-testid={`remove-favorite-${f.id}`}>
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Remove
                            </button>
                        </motion.div>
                    ))}
                </div>
            )}
            <FeedbackFooter />
        </div>
    );
}
